import { useCallback, useEffect, useState } from "react"
import { timeApi } from "../api"
import { useTimer } from "../context/TimerContext"
import { usePermissions } from "../permissions"

/**
 * How much time has been logged against something.
 *
 * ── Why the scope travels with the number ───────────────────────────────────
 *
 * Without `time_tracking:view_all` the server answers with the caller's own
 * entries and nobody else's — correctly, that is the whole point of the
 * permission. But a screen that then prints the figure under the heading "Time
 * logged" is telling an employee that four hours is everything anyone has spent
 * on a task three people are working on.
 *
 * So the response says `scope`, and `label` below is what a heading should read.
 * The caller renders the word rather than choosing it.
 *
 * ── Why it re-reads when the timer changes ──────────────────────────────────
 *
 * Stopping a timer is the single most common way this number changes, and it
 * happens from the bar at the top of the screen rather than from this page. The
 * running entry's id changes on every start and stop, so depending on it means
 * the total updates the moment the work lands, without a poll.
 */
export function useTimeSummary({ taskId, projectId, groupBy = "user", enabled = true }) {
  const { can } = usePermissions()
  const { running } = useTimer()
  const mayView = can("time_tracking", "view")

  const [state, setState] = useState({ groups: [], scope: "own", loading: true, error: null })

  const runningId = running?.id ?? null

  const load = useCallback(async () => {
    if (!enabled || !mayView || (taskId == null && projectId == null)) {
      setState({ groups: [], scope: "own", loading: false, error: null })
      return
    }
    try {
      const data = await timeApi.summary({
        group_by: groupBy,
        ...(taskId != null ? { task_id: taskId } : {}),
        ...(projectId != null ? { project_id: projectId } : {}),
      })
      setState({ groups: data.groups || [], scope: data.scope || "own", loading: false, error: null })
    } catch (err) {
      // A failed read is not zero hours. Saying "0.00 h" because a request
      // timed out is worse than saying nothing, because it looks like an answer.
      setState({ groups: [], scope: "own", loading: false, error: err.userMessage || "Could not load logged time" })
    }
  }, [enabled, mayView, taskId, projectId, groupBy])

  useEffect(() => {
    load()
  }, [load, runningId])

  const trackedSeconds = state.groups.reduce((sum, g) => sum + (Number(g.tracked_seconds) || 0), 0)
  const billableSeconds = state.groups.reduce((sum, g) => sum + (Number(g.billable_seconds) || 0), 0)

  return {
    ...state,
    trackedSeconds,
    billableSeconds,
    /** What a heading over these numbers is allowed to say. */
    label: state.scope === "all" ? "Time logged" : "Your time",
    mayView,
    reload: load,
  }
}
